import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import {
  ArrowLeft,
  Phone,
  Cpu,
  Terminal,
  MessageSquare,
  Users,
  Loader2,
  ExternalLink,
  FileText,
  Calendar,
} from 'lucide-react';

const StorageFileLink = ({ path }: { path: string }) => {
  const [loading, setLoading] = useState(false);
  const handleOpen = async (e: React.MouseEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (path.startsWith('http')) {
        window.open(path, '_blank');
        return;
      }
      const { data, error } = await supabase.storage
        .from('form-uploads')
        .createSignedUrl(path, 3600);
      if (error) throw error;
      if (data?.signedUrl) window.open(data.signedUrl, '_blank');
    } finally {
      setLoading(false);
    }
  };
  const fileName = path.split('/').pop() || 'View File';
  return (
    <button
      onClick={handleOpen}
      disabled={loading}
      className="text-blue-500 hover:underline inline-flex items-center gap-1 text-left"
    >
      {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <ExternalLink className="h-3 w-3" />}
      <span className="truncate max-w-[220px]">{fileName}</span>
    </button>
  );
};

interface SubmissionRow {
  id: string;
  form_id: string;
  data: Record<string, any>;
  status: string;
  created_at: string;
  ai_analysis?: any;
  payment_status?: string;
  completion_time?: number;
  user_agent?: string;
  ip_address?: string;
  device_type?: string;
}

const statusVariant = (s: string): any =>
  s === 'contacted' ? 'success' : s === 'cancelled' ? 'destructive' : s === 'completed' ? 'default' : 'secondary';

const formatDate = (d: string) =>
  new Date(d).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });

const extractContact = (data: Record<string, any>) => {
  let name = '';
  let phone = '';
  let email = '';
  for (const [k, v] of Object.entries(data || {})) {
    const key = k.toLowerCase();
    const val = typeof v === 'string' ? v : '';
    if (!name && (key.includes('name') || key === 'full_name')) name = val;
    if (!phone && (key.includes('phone') || key.includes('whatsapp') || key.includes('mobile'))) phone = val;
    if (!email && key.includes('email')) email = val;
  }
  return { name: name || 'Anonymous', phone, email };
};

const SubmissionDetail = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [submission, setSubmission] = useState<SubmissionRow | null>(null);
  const [formTitle, setFormTitle] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!submissionId) return;
      setLoading(true);
      const { data: sub, error } = await supabase
        .from('submissions')
        .select('*')
        .eq('id', submissionId)
        .maybeSingle();
      if (error || !sub) {
        setSubmission(null);
        setLoading(false);
        return;
      }
      setSubmission(sub as unknown as SubmissionRow);
      const { data: form } = await supabase
        .from('forms')
        .select('title, user_id')
        .eq('id', sub.form_id)
        .maybeSingle();
      if (form) setFormTitle(form.title || '');
      setLoading(false);
    };
    load();
  }, [submissionId]);

  const markContacted = async () => {
    if (!submission) return;
    setUpdating(true);
    const { error } = await supabase
      .from('submissions')
      .update({ status: 'contacted' })
      .eq('id', submission.id);
    setUpdating(false);
    if (error) {
      toast({ title: 'Error', description: 'Failed to update status.', variant: 'destructive' });
      return;
    }
    setSubmission({ ...submission, status: 'contacted' });
    toast({ title: 'Status updated', description: 'Submission marked as contacted.' });
  };

  const contactRespondent = () => {
    if (!submission) return;
    const { name, phone } = extractContact(submission.data || {});
    const cleanPhone = (phone || '').replace(/\D/g, '');
    if (cleanPhone.length < 8) {
      toast({ title: 'No valid phone number', variant: 'destructive' });
      return;
    }
    const message = `Hi ${name}, thank you for your submission via Bravax Forms. We'll get back to you shortly!`;
    window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`, '_blank');
    markContacted();
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  if (!submission) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto py-16 text-center space-y-4">
          <h1 className="text-2xl font-semibold">Submission not found</h1>
          <p className="text-muted-foreground">This response may have been deleted or you don't have access.</p>
          <Button onClick={() => navigate('/submissions')} variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to submissions
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const { name, phone, email } = extractContact(submission.data || {});
  const entries = Object.entries(submission.data || {});
  const humanLabels = entries.filter(([k]) => !k.startsWith('field_') && !/^[0-9a-f-]{36}$/i.test(k));
  const humanValues = new Set(humanLabels.map(([_, v]) => v));
  const filtered = entries.filter(([k, v]) => {
    if ((k.startsWith('field_') || /^[0-9a-f-]{36}$/i.test(k)) && humanValues.has(v)) return false;
    if (k === 'form_id' || k === 'submission_id') return false;
    return true;
  });
  const ai = submission.ai_analysis || null;

  return (
    <DashboardLayout>
      <Helmet>
        <title>Submission · {formTitle || 'Bravax Forms'}</title>
      </Helmet>
      <div className="max-w-5xl mx-auto space-y-6 pb-16">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Submission Details</h1>
              <p className="text-sm text-muted-foreground">
                {formTitle ? (
                  <Link to={`/forms/${submission.form_id}/submissions`} className="hover:underline">
                    {formTitle}
                  </Link>
                ) : 'Form response'} · {formatDate(submission.created_at)}
              </p>
            </div>
          </div>
          <Badge variant={statusVariant(submission.status)} className="capitalize">
            {submission.status || 'pending'}
          </Badge>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Respondent</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <p className="font-medium">{name}</p>
              {email && <p className="text-sm text-muted-foreground break-all">{email}</p>}
              {phone ? (
                <p className="text-sm text-muted-foreground flex items-center">
                  <Phone className="h-3 w-3 mr-1" /> {phone}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">No phone provided</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Submission</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 text-sm">
              <p className="flex items-center gap-2"><FileText className="h-3.5 w-3.5 text-muted-foreground" /> {formTitle || '—'}</p>
              <p className="flex items-center gap-2"><Calendar className="h-3.5 w-3.5 text-muted-foreground" /> {formatDate(submission.created_at)}</p>
              <p className="text-muted-foreground text-xs mt-2">ID: <span className="font-mono">{submission.id.slice(0, 8)}…</span></p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button className="w-full" onClick={contactRespondent}>
                <MessageSquare className="mr-2 h-4 w-4" /> Contact
              </Button>
              <Button variant="outline" className="w-full" onClick={markContacted} disabled={updating || submission.status === 'contacted'}>
                <Users className="mr-2 h-4 w-4" /> Mark as Contacted
              </Button>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Form Responses</CardTitle>
            <CardDescription>All information submitted by the respondent</CardDescription>
          </CardHeader>
          <CardContent>
            {filtered.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {filtered.map(([key, value]) => {
                  const isFile = typeof value === 'string' && (value.startsWith('http') || value.match(/\.(png|jpe?g|pdf|docx?|xlsx?|csv|zip)$/i));
                  return (
                    <div key={key} className="bg-muted/30 p-4 rounded-lg border border-border/50">
                      <Label className="text-[10px] text-muted-foreground font-semibold uppercase tracking-widest">
                        {key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim()}
                      </Label>
                      <div className="mt-1.5 text-sm font-medium break-words">
                        {isFile ? (
                          <StorageFileLink path={String(value)} />
                        ) : (
                          <p className="whitespace-pre-wrap">
                            {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value) || '—'}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-6">No form data available</p>
            )}
          </CardContent>
        </Card>

        {ai && Object.keys(ai).length > 0 && (
          <Card className="border-blue-500/20 bg-blue-500/5">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Cpu className="h-5 w-5 text-blue-600" />
                <CardTitle className="text-lg">AI Analysis Insights</CardTitle>
              </div>
              <CardDescription>Automated processing of submission content</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {ai.sentiment && (
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest">Sentiment</Label>
                    <div><Badge variant={ai.sentiment === 'positive' ? 'success' : ai.sentiment === 'negative' ? 'destructive' : 'secondary'}>{String(ai.sentiment).toUpperCase()}</Badge></div>
                  </div>
                )}
                {ai.priority && (
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest">Priority</Label>
                    <div><Badge variant={ai.priority === 'high' ? 'destructive' : 'outline'}>{String(ai.priority).toUpperCase()}</Badge></div>
                  </div>
                )}
                {ai.intent && (
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest">Intent</Label>
                    <p className="text-sm font-medium capitalize">{ai.intent}</p>
                  </div>
                )}
              </div>
              {ai.summary && (
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest">AI Summary</Label>
                  <p className="text-sm italic text-muted-foreground">"{ai.summary}"</p>
                </div>
              )}
              {ai.key_items?.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest">Key Takeaways</Label>
                  <div className="flex flex-wrap gap-2">
                    {ai.key_items.map((item: string, i: number) => (
                      <Badge key={i} variant="outline" className="text-[10px]">{item}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="bg-zinc-50 dark:bg-zinc-950/50">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Terminal className="h-4 w-4 text-zinc-500" />
              <CardTitle className="text-sm font-medium">Metadata & Telemetry</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase font-bold text-muted-foreground">Device Type</Label>
              <p className="text-sm font-medium capitalize">{submission.device_type || 'Desktop'}</p>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase font-bold text-muted-foreground">IP Address</Label>
              <p className="text-sm font-mono">{submission.ip_address || 'Hidden'}</p>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase font-bold text-muted-foreground">Time to Fill</Label>
              <p className="text-sm font-medium">
                {submission.completion_time
                  ? submission.completion_time > 60
                    ? `${(submission.completion_time / 60).toFixed(1)}m`
                    : `${submission.completion_time}s`
                  : 'N/A'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default SubmissionDetail;
